/**
 * ConditionBuilder V2 - Visual builder for scene trigger/precondition DSL
 *
 * Features:
 * - Searchable sensor picker with current value display
 * - Sentence-like condition rows
 * - Clean Value vs Sensor RHS choice with inline offset
 * - Multi-select day-of-week editor
 * - Daytime/Nighttime quick conditions
 * - Simplified group editing
 * - Human-readable summary
 */

import { useGetApiSettingsHomeLocationSunTimes, useListSensors } from '@smarthome/api';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Clock,
  Calendar,
  Thermometer,
  AlertCircle,
  Sun,
  Moon,
  Search,
  Check,
  X,
  Minus,
} from 'lucide-react';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';

import {
  type ConditionNode,
  type CompareCondition,
  type TimeBetweenCondition,
  type Operand,
  type SensorInfo,
  buildDayOfWeekCondition,
  buildDayNightCondition,
  extractSelectedDays,
  isDayOfWeekGroup,
  parseDayNightCondition,
  generateSummary,
  getSensorDslId,
  validateConditionNode,
} from './conditionUtils';

import { Button, Badge, Toggle } from '@/components/ui';
import { cn } from '@/components/ui/utils';

// Re-export types and validation for external use
export { validateConditionNode };
export type { ConditionNode };

interface ConditionBuilderProps {
  value: ConditionNode | null;
  onChange: (condition: ConditionNode | null) => void;
  label?: string;
  description?: string;
  error?: string;
  allowHysteresis?: boolean;
}

// Synthetic sensors available
const SYNTHETIC_SENSORS: SensorInfo[] = [
  {
    id: 'synthetic.time.local-second-of-day',
    humanId: 'synthetic.time.local-second-of-day',
    name: 'Time of day (seconds)',
    type: 'time',
    group: 'Synthetic',
  },
  {
    id: 'synthetic.time.iso-weekday',
    humanId: 'synthetic.time.iso-weekday',
    name: 'Day of week (1-7)',
    type: 'number',
    group: 'Synthetic',
  },
  {
    id: 'synthetic.time.is-weekend',
    humanId: 'synthetic.time.is-weekend',
    name: 'Weekend',
    type: 'boolean',
    group: 'Synthetic',
  },
];

const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

// Main Component
export function ConditionBuilder({
  value,
  onChange,
  label,
  description,
  error,
  allowHysteresis = false,
}: ConditionBuilderProps) {
  const { data: sensorsData } = useListSensors();
  const realSensors = useMemo(() => sensorsData?.sensors ?? [], [sensorsData?.sensors]);

  const allSensors = useMemo<SensorInfo[]>(() => {
    const mapped: SensorInfo[] = realSensors.map((s) => ({
      id: s.id!,
      humanId: s.humanId ?? s.id!,
      name: s.name ?? s.id!,
      type: 'number' as const,
      group: s.humanId?.startsWith('device:') ? ('Device' as const) : ('Other' as const),
      value: s.value,
      unitSymbol: s.unitSymbol,
    }));
    return [...SYNTHETIC_SENSORS, ...mapped];
  }, [realSensors]);

  const isEmpty = !value || Object.keys(value).length === 0;

  const handleAddCondition = useCallback(() => {
    // Start with an empty "all" group
    onChange({ all: [] });
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const summary = useMemo(() => generateSummary(value, allSensors), [value, allSensors]);

  return (
    <div className="space-y-3">
      {label && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
            {label}
          </label>
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{description}</p>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center dark:border-white/[0.1]">
          <p className="mb-3 text-sm text-gray-500 dark:text-zinc-500">No conditions configured</p>
          <Button variant="outline" size="sm" onClick={handleAddCondition}>
            <Plus className="mr-2 h-4 w-4" />
            Add Condition
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Summary line - with multi-line support */}
          {summary && (
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-zinc-800/50">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Summary:</span>{' '}
              <span className="whitespace-pre-wrap text-gray-600 dark:text-zinc-400">
                {summary}
              </span>
            </div>
          )}

          <ConditionNodeEditor
            node={value}
            onChange={onChange}
            onRemove={handleClear}
            sensors={allSensors}
            isRoot
            allowHysteresis={allowHysteresis}
          />
        </div>
      )}
    </div>
  );
}

// Node Editor Component
interface ConditionNodeEditorProps {
  node: ConditionNode;
  onChange: (node: ConditionNode) => void;
  onRemove: () => void;
  sensors: SensorInfo[];
  isRoot?: boolean;
  allowHysteresis?: boolean;
}

function ConditionNodeEditor({
  node,
  onChange,
  onRemove,
  sensors,
  isRoot,
  allowHysteresis,
}: ConditionNodeEditorProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const dayNight = parseDayNightCondition(node);
  const isGroupNode = !!node.all || !!node.any;
  const isDayGroup = isGroupNode && isDayOfWeekGroup(node);

  if (dayNight) {
    return (
      <QuickConditionRow
        type={dayNight.type}
        startOffsetSeconds={dayNight.startOffsetSeconds}
        endOffsetSeconds={dayNight.endOffsetSeconds}
        onChange={(startOffsetSeconds, endOffsetSeconds) =>
          onChange(buildDayNightCondition(dayNight.type, startOffsetSeconds, endOffsetSeconds))
        }
        onRemove={onRemove}
      />
    );
  }

  // Handle group type (all/any)
  if (isGroupNode && !isDayGroup) {
    return (
      <GroupEditor
        node={node}
        onChange={onChange}
        onRemove={onRemove}
        sensors={sensors}
        isRoot={isRoot}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded(!isExpanded)}
        allowHysteresis={allowHysteresis}
      />
    );
  }

  // Handle day-of-week condition (detected from group structure)
  if (isDayGroup) {
    const selectedDays = extractSelectedDays(node);
    return (
      <DayOfWeekRow
        selectedDays={selectedDays}
        onChange={(days) => onChange(buildDayOfWeekCondition(days))}
        onRemove={onRemove}
      />
    );
  }

  // Handle compare condition
  if (node.compare) {
    return (
      <CompareRow
        condition={node.compare}
        onChange={(c) => onChange({ compare: c })}
        onRemove={onRemove}
        sensors={sensors}
        allowHysteresis={allowHysteresis}
      />
    );
  }

  // Handle timeBetween condition
  if (node.timeBetween) {
    return (
      <TimeBetweenRow
        condition={node.timeBetween}
        onChange={(c) => onChange({ timeBetween: c })}
        onRemove={onRemove}
      />
    );
  }

  // Handle not wrapper
  if (node.not) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="error" size="sm">
            NOT
          </Badge>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
            aria-label="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="ml-4">
          <ConditionNodeEditor
            node={node.not}
            onChange={(n) => onChange({ not: n })}
            onRemove={() => onChange(node.not!)} // Remove the NOT wrapper
            sensors={sensors}
            allowHysteresis={allowHysteresis}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-gray-100 p-3 text-sm text-gray-500 dark:bg-zinc-800">
      Unknown condition type
    </div>
  );
}

// Group Editor Component
interface GroupEditorProps {
  node: ConditionNode;
  onChange: (node: ConditionNode) => void;
  onRemove: () => void;
  sensors: SensorInfo[];
  isRoot?: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  allowHysteresis?: boolean;
}

function GroupEditor({
  node,
  onChange,
  onRemove,
  sensors,
  isRoot,
  isExpanded,
  onToggleExpanded,
  allowHysteresis,
}: GroupEditorProps) {
  const nodeType = node.all ? 'all' : 'any';
  const children = node[nodeType] ?? [];

  const handleAddChild = (type: string) => {
    let newChild: ConditionNode;
    switch (type) {
      case 'compare':
        newChild = {
          compare: {
            left: { sensor: sensors[0]?.humanId ?? '' },
            op: 'gt',
            right: { literal: 0 },
          },
        };
        break;
      case 'timeBetween':
        newChild = { timeBetween: { start: '08:00:00', end: '22:00:00' } };
        break;
      case 'group':
        newChild = { all: [] };
        break;
      case 'dayOfWeek':
        // Default to weekdays
        newChild = buildDayOfWeekCondition([1, 2, 3, 4, 5]);
        break;
      case 'daytime':
        newChild = buildDayNightCondition('daytime', 0, 0);
        break;
      case 'nighttime':
        newChild = buildDayNightCondition('nighttime', 0, 0);
        break;
      case 'not':
        newChild = {
          not: {
            compare: {
              left: { sensor: sensors[0]?.humanId ?? '' },
              op: 'eq',
              right: { literal: 0 },
            },
          },
        };
        break;
      default:
        return;
    }
    onChange({ [nodeType]: [...children, newChild] });
  };

  const handleRemoveChild = (index: number) => {
    const newChildren = children.filter((_, i) => i !== index);
    onChange({ [nodeType]: newChildren });
  };

  const handleUpdateChild = (index: number, newChild: ConditionNode) => {
    const newChildren = [...children];
    newChildren[index] = newChild;
    onChange({ [nodeType]: newChildren });
  };

  const handleToggleType = () => {
    const newType = nodeType === 'all' ? 'any' : 'all';
    onChange({ [newType]: children });
  };

  return (
    <div
      className={cn(
        'rounded-lg border',
        isRoot
          ? 'border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]'
          : 'border-gray-200 bg-gray-50/50 dark:border-white/[0.06] dark:bg-zinc-800/30'
      )}
    >
      {/* Group header */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={handleToggleType}
          className={cn(
            'rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wider transition-colors',
            nodeType === 'all'
              ? 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 dark:text-blue-400'
              : 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400'
          )}
        >
          {nodeType === 'all' ? 'ALL' : 'ANY'}
        </button>
        <span className="flex-1 text-xs text-gray-500 dark:text-zinc-500">
          {children.length === 0
            ? 'No conditions'
            : `${children.length} condition${children.length !== 1 ? 's' : ''}`}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
          aria-label={isRoot ? 'Clear all conditions' : 'Remove group'}
          title={isRoot ? 'Clear all conditions' : 'Remove group'}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Children */}
      {isExpanded && (
        <div className="space-y-2 border-t border-gray-100 p-3 dark:border-white/[0.04]">
          {children.map((child, index) => (
            <ConditionNodeEditor
              key={index}
              node={child}
              onChange={(n) => handleUpdateChild(index, n)}
              onRemove={() => handleRemoveChild(index)}
              sensors={sensors}
              allowHysteresis={allowHysteresis}
            />
          ))}

          {/* Add condition menu */}
          <AddConditionMenu onAdd={handleAddChild} />
        </div>
      )}
    </div>
  );
}

// Add Condition Menu with auto-flip positioning
interface AddConditionMenuProps {
  onAdd: (type: string) => void;
}

function AddConditionMenu({ onAdd }: AddConditionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const menuItems = [
    { type: 'compare', label: 'Sensor', icon: Thermometer, description: 'Compare sensor values' },
    { type: 'timeBetween', label: 'Time', icon: Clock, description: 'Time range condition' },
    {
      type: 'dayOfWeek',
      label: 'Day of week',
      icon: Calendar,
      description: 'Select specific days',
    },
    { type: 'divider' },
    {
      type: 'daytime',
      label: 'Daytime',
      icon: Sun,
      description: 'Sunrise to sunset with adjustable offsets',
    },
    {
      type: 'nighttime',
      label: 'Nighttime',
      icon: Moon,
      description: 'Sunset to sunrise with adjustable offsets',
    },
    { type: 'divider' },
    { type: 'group', label: 'Group', icon: Plus, description: 'Nested AND/OR group' },
    { type: 'not', label: 'NOT', icon: X, description: 'Negate a condition' },
  ];

  // Calculate if menu should open upward
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const estimatedMenuHeight = 320; // Approximate menu height

    setOpenUpward(spaceBelow < estimatedMenuHeight && spaceAbove > estimatedMenuHeight);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (type: string) => {
    onAdd(type);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <Button variant="ghost" size="sm" onClick={handleToggle}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add condition
        <ChevronDown
          className={cn('ml-1.5 h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')}
        />
      </Button>

      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-50 w-[calc(100vw-2rem)] max-w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b] sm:w-56',
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {menuItems.map((item, index) =>
            item.type === 'divider' ? (
              <div key={index} className="my-1 border-t border-gray-200 dark:border-white/[0.06]" />
            ) : (
              <button
                type="button"
                key={item.type}
                onClick={() => handleSelect(item.type)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.04]"
              >
                {item.icon && <item.icon className="h-4 w-4 text-gray-500 dark:text-zinc-500" />}
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">{item.label}</div>
                  <div className="text-xs text-gray-500 dark:text-zinc-500">{item.description}</div>
                </div>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Sensor Picker Component with auto-flip positioning
interface SensorPickerProps {
  value: string;
  onChange: (sensorId: string) => void;
  sensors: SensorInfo[];
  placeholder?: string;
  className?: string;
}

function SensorPicker({ value, onChange, sensors, placeholder, className }: SensorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSensor = sensors.find((s) => s.humanId === value || s.id === value);

  // Group and filter sensors
  const filteredSensors = useMemo(() => {
    const searchLower = search.toLowerCase();
    return sensors.filter(
      (s) =>
        s.name.toLowerCase().includes(searchLower) || s.humanId.toLowerCase().includes(searchLower)
    );
  }, [sensors, search]);

  const groupedSensors = useMemo(() => {
    const groups: { Synthetic: SensorInfo[]; Device: SensorInfo[]; Other: SensorInfo[] } = {
      Synthetic: [],
      Device: [],
      Other: [],
    };
    filteredSensors.forEach((s) => {
      groups[s.group].push(s);
    });
    return groups;
  }, [filteredSensors]);

  // Calculate if menu should open upward
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const estimatedMenuHeight = 320; // max-h-64 = 256px + search bar

    setOpenUpward(spaceBelow < estimatedMenuHeight && spaceAbove > estimatedMenuHeight);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (sensor: SensorInfo) => {
    onChange(getSensorDslId(sensor));
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-lg border px-3 text-sm transition-colors',
          'border-gray-300 bg-gray-50 hover:border-gray-400',
          'dark:border-white/[0.06] dark:bg-[#18181b] dark:hover:border-white/[0.1]',
          selectedSensor ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-zinc-500'
        )}
      >
        <span className="truncate">
          {selectedSensor?.name ?? placeholder ?? 'Select sensor...'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-shrink-0 text-gray-500 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-50 w-[calc(100vw-2rem)] max-w-80 rounded-lg border border-gray-200 bg-white shadow-xl dark:border-white/[0.06] dark:bg-[#18181b] sm:w-80',
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {/* Search input */}
          <div className="border-b border-gray-200 p-2 dark:border-white/[0.06]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-zinc-500" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search sensors..."
                className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
              />
            </div>
          </div>

          {/* Sensor list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {Object.entries(groupedSensors).map(([group, items]) => {
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                    {group}
                  </div>
                  {items.map((sensor) => (
                    <button
                      type="button"
                      key={sensor.id}
                      onClick={() => handleSelect(sensor)}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.04]',
                        (selectedSensor?.id === sensor.id ||
                          selectedSensor?.humanId === sensor.humanId) &&
                          'bg-blue-50 dark:bg-blue-500/10'
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {sensor.name}
                        </div>
                        <div className="flex items-center gap-2 truncate text-xs text-gray-500 dark:text-zinc-500">
                          <span className="truncate">{sensor.humanId}</span>
                          {sensor.value !== undefined && (
                            <>
                              <span>•</span>
                              <span>
                                {sensor.value}
                                {sensor.unitSymbol ? ` ${sensor.unitSymbol}` : ''}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {(selectedSensor?.id === sensor.id ||
                        selectedSensor?.humanId === sensor.humanId) && (
                        <Check className="h-4 w-4 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                  ))}
                </div>
              );
            })}
            {filteredSensors.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-zinc-500">
                No sensors found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// DecimalInput - handles decimal number input properly by maintaining string state
interface DecimalInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
}

function DecimalInput({ value, onChange, className, placeholder }: DecimalInputProps) {
  const [localValue, setLocalValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync from parent when value changes externally
  useEffect(() => {
    // Only sync if the input is not focused (user is not typing)
    if (document.activeElement !== inputRef.current) {
      setLocalValue(String(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Parse and update parent, but allow intermediate states
    const parsed = parseFloat(newValue);
    if (!isNaN(parsed)) {
      onChange(parsed);
    } else if (newValue === '' || newValue === '-') {
      onChange(0);
    }
  };

  const handleBlur = () => {
    // On blur, ensure we have a valid number and sync state
    const parsed = parseFloat(localValue);
    if (isNaN(parsed) || localValue === '') {
      setLocalValue('0');
      onChange(0);
    } else {
      // Clean up the display (remove trailing dots, etc.)
      setLocalValue(String(parsed));
      onChange(parsed);
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
    />
  );
}

// OffsetInput - handles decimal input for offset values
interface OffsetInputProps {
  value: number;
  sign: '+' | '-';
  onChange: (value: number, sign: '+' | '-') => void;
}

function OffsetInput({ value, sign, onChange }: OffsetInputProps) {
  const [localValue, setLocalValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync from parent when value changes externally
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(String(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    const parsed = parseFloat(newValue);
    if (!isNaN(parsed)) {
      onChange(parsed, sign);
    } else if (newValue === '' || newValue === '-') {
      onChange(0, sign);
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(localValue);
    if (isNaN(parsed) || localValue === '') {
      setLocalValue('0');
      onChange(0, sign);
    } else {
      setLocalValue(String(parsed));
      onChange(parsed, sign);
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className="w-16 border-0 bg-transparent px-1 py-1 text-center text-sm text-gray-900 focus:outline-none focus:ring-0 dark:text-white"
      placeholder="0"
    />
  );
}

// Compact operator select options
const OPERATOR_OPTIONS = [
  { value: 'eq', label: '= equals' },
  { value: 'ne', label: '≠ not equal' },
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '≥ greater or equal' },
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '≤ less or equal' },
];

// Compare Row Component - Redesigned with 2-row layout
interface CompareRowProps {
  condition: CompareCondition;
  onChange: (condition: CompareCondition) => void;
  onRemove: () => void;
  sensors: SensorInfo[];
  allowHysteresis?: boolean;
}

function CompareRow({ condition, onChange, onRemove, sensors, allowHysteresis }: CompareRowProps) {
  // Parse the condition
  const leftSensorId = useMemo(() => {
    const left = condition.left as { sensor?: string };
    return left.sensor ?? '';
  }, [condition.left]);

  const rightInfo = useMemo(() => {
    const right = condition.right as {
      literal?: number;
      sensor?: string;
      add?: [Operand, Operand];
      sub?: [Operand, Operand];
    };

    if (right.literal !== undefined) {
      return { mode: 'value' as const, value: right.literal };
    }
    if (right.sensor) {
      return {
        mode: 'sensor' as const,
        sensorId: right.sensor,
        offset: 0,
        offsetSign: '+' as const,
      };
    }
    if (right.add) {
      const [base, offset] = right.add;
      const baseSensor = (base as { sensor?: string }).sensor ?? '';
      const offsetValue = (offset as { literal?: number }).literal ?? 0;
      return {
        mode: 'sensor' as const,
        sensorId: baseSensor,
        offset: offsetValue,
        offsetSign: '+' as const,
      };
    }
    if (right.sub) {
      const [base, offset] = right.sub;
      const baseSensor = (base as { sensor?: string }).sensor ?? '';
      const offsetValue = (offset as { literal?: number }).literal ?? 0;
      return {
        mode: 'sensor' as const,
        sensorId: baseSensor,
        offset: offsetValue,
        offsetSign: '-' as const,
      };
    }
    return { mode: 'value' as const, value: 0 };
  }, [condition.right]);

  const leftSensor = sensors.find((s) => s.humanId === leftSensorId || s.id === leftSensorId);
  const isBooleanSensor = leftSensor?.type === 'boolean';
  const hysteresisEnabled = Boolean(condition.hysteresis);
  const supportsOffThreshold = condition.op !== 'eq' && condition.op !== 'ne';
  const canConfigureHysteresis =
    Boolean(allowHysteresis) &&
    supportsOffThreshold &&
    !isBooleanSensor &&
    rightInfo.mode === 'value';
  const offOperatorLabels: Record<CompareCondition['op'], string> = {
    gt: '≤',
    gte: '<',
    lt: '≥',
    lte: '>',
    eq: '?',
    ne: '?',
  };
  const offOpLabel = offOperatorLabels[condition.op] ?? '?';

  const getDefaultRelease = (activation: number, op: CompareCondition['op']): number => {
    const delta = 1;
    if (op === 'gt' || op === 'gte') {
      return activation - delta;
    }
    if (op === 'lt' || op === 'lte') {
      return activation + delta;
    }
    return activation;
  };

  // Handlers
  const handleLeftChange = (sensorId: string) => {
    onChange({ ...condition, left: { sensor: sensorId } });
  };

  const handleOpChange = (op: string) => {
    const nextOp = op as CompareCondition['op'];
    const nextCondition: CompareCondition = { ...condition, op: nextOp };
    if (nextOp === 'eq' || nextOp === 'ne') {
      nextCondition.hysteresis = undefined;
    }
    onChange(nextCondition);
  };

  const handleRhsModeChange = (mode: string) => {
    if (mode === 'value') {
      onChange({ ...condition, right: { literal: 0 } });
    } else {
      onChange({
        ...condition,
        right: { sensor: sensors[0]?.humanId ?? '' },
        hysteresis: undefined,
      });
    }
  };

  const handleRhsValueChange = (value: number) => {
    onChange({ ...condition, right: { literal: value } });
  };

  const handleRhsSensorChange = (sensorId: string) => {
    if (rightInfo.mode === 'sensor' && rightInfo.offset !== 0) {
      const newRight: Operand =
        rightInfo.offsetSign === '+'
          ? { add: [{ sensor: sensorId }, { literal: rightInfo.offset }] }
          : { sub: [{ sensor: sensorId }, { literal: rightInfo.offset }] };
      onChange({ ...condition, right: newRight });
    } else {
      onChange({ ...condition, right: { sensor: sensorId } });
    }
  };

  const handleOffsetChange = (offset: number, sign: '+' | '-') => {
    if (rightInfo.mode !== 'sensor') return;

    if (offset === 0) {
      // Remove offset wrapper
      onChange({ ...condition, right: { sensor: rightInfo.sensorId } });
    } else {
      const newRight: Operand =
        sign === '+'
          ? { add: [{ sensor: rightInfo.sensorId }, { literal: offset }] }
          : { sub: [{ sensor: rightInfo.sensorId }, { literal: offset }] };
      onChange({ ...condition, right: newRight });
    }
  };

  const handleHysteresisToggle = (checked: boolean) => {
    if (!checked) {
      onChange({ ...condition, hysteresis: undefined });
      return;
    }
    if (!canConfigureHysteresis) return;
    const activationValue = rightInfo.mode === 'value' ? rightInfo.value : 0;
    const releaseValue = getDefaultRelease(activationValue, condition.op);
    onChange({ ...condition, hysteresis: { release: releaseValue } });
  };

  const handleReleaseChange = (value: number) => {
    if (!condition.hysteresis) return;
    onChange({ ...condition, hysteresis: { release: value } });
  };

  const currentOpLabel = OPERATOR_OPTIONS.find((o) => o.value === condition.op)?.label ?? '=';

  return (
    <div className="grid grid-cols-[1fr_auto] gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-800/50">
      {/* Left: Editor content */}
      <div className="space-y-3">
        {/* Row 1: Sensor picker */}
        <SensorPicker
          value={leftSensorId}
          onChange={handleLeftChange}
          sensors={sensors}
          placeholder="Select sensor..."
        />

        {/* Row 2: Operator + RHS */}
        <div className="flex flex-wrap items-center gap-2">
          {isBooleanSensor ? (
            /* Boolean: simple Yes/No toggle - h-9 for consistency */
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-zinc-500">is</span>
              <div className="flex h-9 items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 dark:border-white/[0.06] dark:bg-zinc-900">
                <button
                  type="button"
                  onClick={() => onChange({ ...condition, op: 'eq', right: { literal: 1 } })}
                  className={cn(
                    'h-7 rounded px-3 text-sm font-medium transition-colors',
                    (condition.right as { literal?: number }).literal === 1
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                  )}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ ...condition, op: 'eq', right: { literal: 0 } })}
                  className={cn(
                    'h-7 rounded px-3 text-sm font-medium transition-colors',
                    (condition.right as { literal?: number }).literal === 0
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                  )}
                >
                  No
                </button>
              </div>
            </div>
          ) : (
            /* Numeric: Operator select + RHS - all controls use h-9 for consistency */
            <>
              {/* Compact operator dropdown */}
              <OperatorSelect
                value={condition.op}
                onChange={handleOpChange}
                label={currentOpLabel}
              />

              {/* RHS mode selector - custom inline for consistent height */}
              <div className="flex h-9 items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 dark:border-white/[0.06] dark:bg-zinc-900">
                <button
                  type="button"
                  onClick={() => handleRhsModeChange('value')}
                  className={cn(
                    'h-7 rounded px-3 text-sm font-medium transition-colors',
                    rightInfo.mode === 'value'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                  )}
                >
                  Value
                </button>
                <button
                  type="button"
                  onClick={() => handleRhsModeChange('sensor')}
                  className={cn(
                    'h-7 rounded px-3 text-sm font-medium transition-colors',
                    rightInfo.mode === 'sensor'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                  )}
                >
                  Sensor
                </button>
              </div>

              {/* RHS value input */}
              {rightInfo.mode === 'value' ? (
                <DecimalInput
                  value={rightInfo.value}
                  onChange={handleRhsValueChange}
                  className="h-9 w-24 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white"
                  placeholder="0"
                />
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <SensorPicker
                    value={rightInfo.sensorId}
                    onChange={handleRhsSensorChange}
                    sensors={sensors}
                    className="min-w-[160px]"
                  />
                  {/* Offset controls */}
                  <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-1 py-0.5 dark:border-white/[0.06] dark:bg-zinc-900">
                    <button
                      type="button"
                      onClick={() => handleOffsetChange(Math.abs(rightInfo.offset ?? 0), '+')}
                      className={cn(
                        'rounded px-1.5 py-1 text-sm transition-colors',
                        rightInfo.offsetSign === '+' && rightInfo.offset !== 0
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                      )}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOffsetChange(Math.abs(rightInfo.offset ?? 0), '-')}
                      className={cn(
                        'rounded px-1.5 py-1 text-sm transition-colors',
                        rightInfo.offsetSign === '-' && rightInfo.offset !== 0
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                      )}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <OffsetInput
                      value={rightInfo.offset ?? 0}
                      sign={rightInfo.offsetSign ?? '+'}
                      onChange={handleOffsetChange}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {allowHysteresis && !isBooleanSensor && supportsOffThreshold && (
          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-white/[0.06] dark:bg-zinc-900">
            <Toggle
              size="sm"
              label="Use separate off threshold"
              description={
                rightInfo.mode === 'value'
                  ? 'Keeps the scene on until the value crosses the off threshold.'
                  : 'Switch to Value on the right side to enable this.'
              }
              checked={hysteresisEnabled}
              onChange={handleHysteresisToggle}
              disabled={!canConfigureHysteresis}
              layout="switch-right"
            />

            {hysteresisEnabled && rightInfo.mode === 'value' && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-zinc-400">
                <span>Turn off when value {offOpLabel}</span>
                <DecimalInput
                  value={condition.hysteresis?.release ?? 0}
                  onChange={handleReleaseChange}
                  className="h-8 w-24 rounded-lg border border-gray-300 bg-gray-50 px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-[#18181b] dark:text-white"
                  placeholder="0"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: Delete button - aligned */}
      <div className="flex items-start pt-1">
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
          aria-label="Remove condition"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Compact Operator Select dropdown with auto-flip
interface OperatorSelectProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

function OperatorSelect({ value, onChange, label }: OperatorSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const estimatedMenuHeight = 280;

    setOpenUpward(spaceBelow < estimatedMenuHeight && spaceAbove > estimatedMenuHeight);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:border-gray-400 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-white/[0.1]"
      >
        <span>{label.split(' ')[0]}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div
          className={cn(
            'absolute left-0 z-50 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]',
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {OPERATOR_OPTIONS.map((op) => (
            <button
              type="button"
              key={op.value}
              onClick={() => {
                onChange(op.value);
                setIsOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100 dark:hover:bg-white/[0.04]',
                value === op.value &&
                  'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'
              )}
            >
              {value === op.value && <Check className="h-4 w-4" />}
              <span className={value !== op.value ? 'ml-6' : ''}>{op.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Time Between Row Component
interface TimeBetweenRowProps {
  condition: TimeBetweenCondition;
  onChange: (condition: TimeBetweenCondition) => void;
  onRemove: () => void;
}

function TimeBetweenRow({ condition, onChange, onRemove }: TimeBetweenRowProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-800/50">
      <div className="flex items-start gap-2">
        <Clock className="mt-2 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-zinc-500" />
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-zinc-400">Between</span>
          <input
            type="time"
            step="1"
            value={condition.start.slice(0, 8)}
            onChange={(e) => onChange({ ...condition, start: e.target.value || '00:00:00' })}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-white"
          />
          <span className="text-sm text-gray-600 dark:text-zinc-400">and</span>
          <input
            type="time"
            step="1"
            value={condition.end.slice(0, 8)}
            onChange={(e) => onChange({ ...condition, end: e.target.value || '00:00:00' })}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-white"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
          aria-label="Remove condition"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <p className="ml-6 mt-1.5 text-xs text-gray-500 dark:text-zinc-500">
        Evaluated in home location timezone (local time)
      </p>
    </div>
  );
}

// Day of Week Row Component
interface DayOfWeekRowProps {
  selectedDays: number[];
  onChange: (days: number[]) => void;
  onRemove: () => void;
}

function DayOfWeekRow({ selectedDays, onChange, onRemove }: DayOfWeekRowProps) {
  const isWeekdays =
    selectedDays.length === 5 && [1, 2, 3, 4, 5].every((d) => selectedDays.includes(d));
  const isWeekend = selectedDays.length === 2 && [6, 7].every((d) => selectedDays.includes(d));
  const isAllDays = selectedDays.length === 7;

  const toggleDay = (day: number) => {
    if (selectedDays.includes(day)) {
      onChange(selectedDays.filter((d) => d !== day));
    } else {
      onChange([...selectedDays, day].sort());
    }
  };

  const setPreset = (preset: 'weekdays' | 'weekend' | 'all') => {
    switch (preset) {
      case 'weekdays':
        onChange([1, 2, 3, 4, 5]);
        break;
      case 'weekend':
        onChange([6, 7]);
        break;
      case 'all':
        onChange([1, 2, 3, 4, 5, 6, 7]);
        break;
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-800/50">
      <div className="flex items-start gap-2">
        <Calendar className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-zinc-500" />
        <div className="flex-1 space-y-2">
          {/* Day chips */}
          <div className="flex flex-wrap gap-1">
            {DAYS_OF_WEEK.map((day) => (
              <button
                type="button"
                key={day.value}
                onClick={() => toggleDay(day.value)}
                className={cn(
                  'rounded-full px-3 py-1 text-sm font-medium transition-colors',
                  selectedDays.includes(day.value)
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600'
                )}
              >
                {day.label}
              </button>
            ))}
          </div>

          {/* Preset buttons */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-zinc-500">Presets:</span>
            <button
              type="button"
              onClick={() => setPreset('weekdays')}
              className={cn(
                'rounded px-2 py-0.5 text-xs transition-colors',
                isWeekdays
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'text-gray-600 hover:bg-gray-200 dark:text-zinc-400 dark:hover:bg-zinc-700'
              )}
            >
              Weekdays
            </button>
            <button
              type="button"
              onClick={() => setPreset('weekend')}
              className={cn(
                'rounded px-2 py-0.5 text-xs transition-colors',
                isWeekend
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'text-gray-600 hover:bg-gray-200 dark:text-zinc-400 dark:hover:bg-zinc-700'
              )}
            >
              Weekend
            </button>
            <button
              type="button"
              onClick={() => setPreset('all')}
              className={cn(
                'rounded px-2 py-0.5 text-xs transition-colors',
                isAllDays
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
                  : 'text-gray-600 hover:bg-gray-200 dark:text-zinc-400 dark:hover:bg-zinc-700'
              )}
            >
              All days
            </button>
          </div>

          {/* Validation messages */}
          {selectedDays.length === 0 && (
            <p className="text-xs text-red-500 dark:text-red-400">Select at least one day</p>
          )}
          {isAllDays && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              All days selected — this condition is always true
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
          aria-label="Remove condition"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Quick Condition Row (Daytime/Nighttime)
interface QuickConditionRowProps {
  type: 'daytime' | 'nighttime';
  startOffsetSeconds: number;
  endOffsetSeconds: number;
  onChange: (startOffsetSeconds: number, endOffsetSeconds: number) => void;
  onRemove: () => void;
}

function formatSecondsOfDay(seconds: number): string {
  const secondsPerDay = 24 * 60 * 60;
  const normalized = ((seconds % secondsPerDay) + secondsPerDay) % secondsPerDay;
  const hours = Math.floor(normalized / 3600);
  const minutes = Math.floor((normalized % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function formatSecondsWithShift(seconds: number): string {
  const secondsPerDay = 24 * 60 * 60;
  const shift = Math.floor(seconds / secondsPerDay);
  const time = formatSecondsOfDay(seconds);
  if (shift === 0) return time;
  const sign = shift > 0 ? '+' : '';
  return `${time} (${sign}${shift}d)`;
}

function formatOffsetMinutes(offsetSeconds: number): string {
  const minutes = offsetSeconds / 60;
  const absMinutes = Math.abs(minutes);
  const formatted = Number.isInteger(absMinutes)
    ? absMinutes.toString()
    : absMinutes.toFixed(1).replace(/\.0$/, '');
  const sign = minutes < 0 ? '-' : '+';
  return `${sign}${formatted}m`;
}

function QuickConditionRow({
  type,
  startOffsetSeconds,
  endOffsetSeconds,
  onChange,
  onRemove,
}: QuickConditionRowProps) {
  const {
    data: sunTimesData,
    isLoading,
    isError,
  } = useGetApiSettingsHomeLocationSunTimes({
    query: { staleTime: 30 * 60 * 1000 },
  });

  const config = {
    daytime: {
      icon: Sun,
      label: 'Daytime',
      description: 'Sun is above the horizon with optional offsets',
      bgClass: 'bg-amber-50 dark:bg-amber-500/10',
      iconClass: 'text-amber-500 dark:text-amber-400',
      borderClass: 'border-amber-200 dark:border-amber-500/20',
    },
    nighttime: {
      icon: Moon,
      label: 'Nighttime',
      description: 'Sun is below the horizon with optional offsets',
      bgClass: 'bg-indigo-50 dark:bg-indigo-500/10',
      iconClass: 'text-indigo-500 dark:text-indigo-400',
      borderClass: 'border-indigo-200 dark:border-indigo-500/20',
    },
  };

  const { icon: Icon, label, description, bgClass, iconClass, borderClass } = config[type];
  const startLabel = type === 'daytime' ? 'Sunrise' : 'Sunset';
  const endLabel = type === 'daytime' ? 'Sunset' : 'Sunrise';
  const sunriseSeconds =
    typeof sunTimesData?.sunriseSeconds === 'number' ? sunTimesData.sunriseSeconds : null;
  const sunsetSeconds =
    typeof sunTimesData?.sunsetSeconds === 'number' ? sunTimesData.sunsetSeconds : null;
  const baseStartSeconds = type === 'daytime' ? sunriseSeconds : sunsetSeconds;
  const baseEndSeconds = type === 'daytime' ? sunsetSeconds : sunriseSeconds;
  const startOffsetLabel = formatOffsetMinutes(startOffsetSeconds);
  const endOffsetLabel = formatOffsetMinutes(endOffsetSeconds);
  const exampleText =
    baseStartSeconds !== null && baseEndSeconds !== null
      ? `${startLabel} ${formatSecondsOfDay(baseStartSeconds)} -> start ${formatSecondsWithShift(
          baseStartSeconds + startOffsetSeconds
        )} (${startOffsetLabel}) | ${endLabel} ${formatSecondsOfDay(
          baseEndSeconds
        )} -> end ${formatSecondsWithShift(baseEndSeconds + endOffsetSeconds)} (${endOffsetLabel})`
      : null;
  const configured = sunTimesData?.configured ?? false;
  let statusText = '';
  if (isLoading) {
    statusText = 'Loading sunrise/sunset times...';
  } else if (isError) {
    statusText = 'Sunrise/sunset times unavailable.';
  } else if (!configured) {
    statusText = 'Set a home location to show sunrise and sunset times.';
  } else {
    statusText = exampleText ?? 'Sunrise/sunset times unavailable.';
  }

  const startSign: '+' | '-' = startOffsetSeconds < 0 ? '-' : '+';
  const endSign: '+' | '-' = endOffsetSeconds < 0 ? '-' : '+';
  const startMinutes = Math.abs(startOffsetSeconds) / 60;
  const endMinutes = Math.abs(endOffsetSeconds) / 60;

  const handleStartOffsetChange = (value: number, sign: '+' | '-') => {
    const seconds = value * 60 * (sign === '-' ? -1 : 1);
    onChange(seconds, endOffsetSeconds);
  };

  const handleEndOffsetChange = (value: number, sign: '+' | '-') => {
    const seconds = value * 60 * (sign === '-' ? -1 : 1);
    onChange(startOffsetSeconds, seconds);
  };

  return (
    <div className={cn('flex items-center gap-3 rounded-lg border p-3', bgClass, borderClass)}>
      <Icon className={cn('h-5 w-5', iconClass)} />
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
        <div className="text-xs text-gray-500 dark:text-zinc-500">{description}</div>
        <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-500">{statusText}</div>
        <div className="mt-3 grid gap-2 text-xs text-gray-600 dark:text-zinc-400 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5 dark:border-white/[0.06] dark:bg-zinc-900">
            <div>
              <div className="font-medium text-gray-700 dark:text-zinc-300">Start</div>
              <div className="text-[11px] text-gray-500 dark:text-zinc-500">{startLabel}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleStartOffsetChange(Math.abs(startMinutes), '-')}
                className={cn(
                  'rounded px-1.5 py-1 text-sm transition-colors',
                  startSign === '-' && startOffsetSeconds !== 0
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                )}
                aria-label="Negative start offset"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleStartOffsetChange(Math.abs(startMinutes), '+')}
                className={cn(
                  'rounded px-1.5 py-1 text-sm transition-colors',
                  startSign === '+' && startOffsetSeconds !== 0
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                )}
                aria-label="Positive start offset"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <OffsetInput
                value={startMinutes}
                sign={startSign}
                onChange={handleStartOffsetChange}
              />
              <span className="pr-1 text-[11px] text-gray-500 dark:text-zinc-500">min</span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5 dark:border-white/[0.06] dark:bg-zinc-900">
            <div>
              <div className="font-medium text-gray-700 dark:text-zinc-300">End</div>
              <div className="text-[11px] text-gray-500 dark:text-zinc-500">{endLabel}</div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleEndOffsetChange(Math.abs(endMinutes), '-')}
                className={cn(
                  'rounded px-1.5 py-1 text-sm transition-colors',
                  endSign === '-' && endOffsetSeconds !== 0
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                )}
                aria-label="Negative end offset"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleEndOffsetChange(Math.abs(endMinutes), '+')}
                className={cn(
                  'rounded px-1.5 py-1 text-sm transition-colors',
                  endSign === '+' && endOffsetSeconds !== 0
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                )}
                aria-label="Positive end offset"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <OffsetInput value={endMinutes} sign={endSign} onChange={handleEndOffsetChange} />
              <span className="pr-1 text-[11px] text-gray-500 dark:text-zinc-500">min</span>
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="rounded p-1.5 text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
        aria-label="Remove condition"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}
